import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { BlogHead } from "@/components/seo/BlogHead";
import { BlogPostContent } from "@/components/blog/BlogPostContent";
import BlogArticleTocLite from "@/components/blog/BlogArticleTocLite";
import { getPostBySlug, type BlogPost } from "@/lib/blog";

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      return;
    }

    const foundPost = getPostBySlug(slug);
    if (!foundPost) {
      setNotFound(true);
      return;
    }

    // For draft posts, only show in non-production
    if (foundPost.status === 'draft' && window.location.hostname === 'www.cosmos-wave.com') {
      setNotFound(true);
      return;
    }

    setPost(foundPost);
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Article non trouvé</h1>
          <p className="text-muted-foreground mb-8">Cet article n'existe pas ou n'est pas encore publié.</p>
          <Link to="/blog" className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span>Retour au blog</span>
          </Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement de l'article...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <BlogHead
        title={post.title}
        description={post.description}
        slug={post.slug}
        datePublished={post.datePublished}
        dateModified={post.dateModified}
        author={post.author}
        cover={post.cover}
        tags={post.tags}
      />

      

      {/* Article Header */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-primary/10" />
        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto text-center">
            {post.status === 'draft' && (
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 mb-6">
                <span className="text-orange-600 font-medium text-sm">🚧 Article en préparation</span>
              </div>
            )}
            
            <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
              {post.title}
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed">
              {post.description}
            </p>
            
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground mb-8">
              <span>Par {post.author}</span>
              <span>•</span>
              <span>Publié le {new Date(post.datePublished).toLocaleDateString('fr-FR')}</span>
              {post.datePublished !== post.dateModified && (
                <>
                  <span>•</span>
                  <span>Mis à jour le {new Date(post.dateModified).toLocaleDateString('fr-FR')}</span>
                </>
              )}
            </div>
            
            <div className="flex flex-wrap gap-2 justify-center">
              {post.tags.map(tag => (
                <Badge key={tag} variant="secondary">{tag}</Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TOC and Reading Progress */}
      <BlogArticleTocLite />

      {/* Article Content */}
      <BlogPostContent post={post} />

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-primary/10 via-primary/5 to-background relative overflow-hidden">
        <div className="container mx-auto px-6 text-center relative">
          <h2 className="text-3xl font-bold mb-4">
            Besoin d'aide pour implémenter ces stratégies ?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Nos experts Amazon SPN vous accompagnent dans la mise en œuvre de ces techniques avancées.
          </p>
          <Link 
            to="/audit-gratuit"
            className="inline-flex items-center px-8 py-4 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors"
          >
            Obtenir un audit gratuit
          </Link>
        </div>
      </section>
    </div>
  );
}